// Copyright ©️ 2026 eVolve MEP, LLC

export const hasValidTolerance = (tolerance: number) => {
  if (tolerance <= 0 || tolerance > 1) {
    // oxlint-disable-next-line no-console
    console.error(`Tolerance must be a value greater than 0 and less than or equal to 1. Received: "${tolerance}"`);
    return false;
  }
  // tolerance must be 1 / (some number that is a power of 2).
  if (!Number.isSafeInteger(Math.log(1 / tolerance) / Math.log(2))) {
    // oxlint-disable-next-line no-console
    console.error(`Tolerance must be a base-2 fraction (e.g., 1/2, 1/4, 1/8, etc.) Received: "1/${1 / tolerance}"`);
    return false;
  }
  return true;
};

const getFormattedResult = (isNegative: boolean, feet: number, inches: number, fractionalInchesString: string) => {
  const sign = isNegative ? '-' : '';
  const hasFeet = feet > 0;
  const hasInches = inches > 0;
  const hasFraction = Boolean(fractionalInchesString);

  const parts: string[] = [];
  if (hasFeet) {
    parts.push(`${feet}'`);
  }
  let inchesPart = '';
  if (hasInches) {
    inchesPart = hasFraction ? `${inches}-${fractionalInchesString}"` : `${inches}"`;
  } else if (hasFraction) {
    inchesPart = `${fractionalInchesString}"`;
  }
  if (inchesPart) {
    parts.push(inchesPart);
  }

  if (parts.length === 0) {
    return `0"`;
  }
  return `${sign}${parts.join(' ')}`;
};

const greatestCommonDenominator = (a: number, b: number): number => (b === 0 ? a : greatestCommonDenominator(b, a % b));

const getFractionString = (numerator: number, denominator: number) => {
  if (numerator === 0 || numerator === denominator) return '';
  const gcd = greatestCommonDenominator(numerator, denominator);
  return `${numerator / gcd}/${denominator / gcd}`;
};

/**
 * Converts a decimal number in inches to a formatted imperial string with fractional inches (e.g., '1/4' or '1/8').
 *
 * The fractional part is rounded to the nearest base-2 fraction within the specified tolerance.
 *
 * To round to the nearest whole inch, set tolerance to 1.
 *
 * @example If the value is 0.1 inches and the tolerance is 1/16, the result will be '1/8"' instead of '1/10"'.
 *
 * @example If the value is 0.1 inches and the tolerance is 1/4, the result will be '0"'.
 * */
export const convertInchesToImperialString = (
  valueInInches: number,
  { inchesOnly = true, tolerance = 1 / 16 } = {},
) => {
  // If the tolerance is invalid, default to 1 (round to nearest whole inch).
  const validatedTolerance = hasValidTolerance(tolerance) ? tolerance : 1;

  const isNegative = valueInInches < 0;
  const absValueInInches = Math.abs(valueInInches);
  let wholeNumberInInches = Math.floor(absValueInInches);

  const fractionalPartInInches = absValueInInches - wholeNumberInInches;
  const denominator = 1 / validatedTolerance;
  const numerator = Math.round(fractionalPartInInches * denominator);
  const shouldRoundToWholeInch = numerator === denominator;
  if (shouldRoundToWholeInch) {
    wholeNumberInInches += 1;
  }

  const feet = inchesOnly ? 0 : Math.floor(wholeNumberInInches / 12);
  const inches = inchesOnly ? wholeNumberInInches : wholeNumberInInches % 12;

  const fractionalInchesString = getFractionString(numerator, denominator);

  return getFormattedResult(isNegative, feet, inches, fractionalInchesString);
};
